import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import PageHeader from "@/components/common/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { fmtDate, fmtNum } from "@/lib/format";
import { toast } from "sonner";
import {
  Play, Factory, ShoppingCart, FileText, Sparkles, Save,
  AlertTriangle, Clock, Calendar, Zap, TrendingUp, ChevronRight, ExternalLink,
} from "lucide-react";
import { useSubscription } from "@/context/SubscriptionContext";

/* ------------------------------------------------------------------ helpers */
const inr = (n) => `₹${fmtNum(Math.round(Number(n) || 0))}`;
const clsBadgeCrit = "bg-rose-50 text-rose-700 border border-rose-200";
const clsBadgeLong = "bg-amber-50 text-amber-700 border border-amber-200";
const clsBadgeBuy  = "bg-sky-50 text-sky-700 border border-sky-200";
const clsBadgeMake = "bg-emerald-50 text-emerald-700 border border-emerald-200";

const rollingMonths = (n = 6) => {
  const out = [];
  const d = new Date(); d.setDate(1);
  for (let i = 0; i < n; i++) {
    const y = d.getFullYear(), m = String(d.getMonth() + 1).padStart(2, "0");
    out.push(`${y}-${m}`);
    d.setMonth(d.getMonth() + 1);
  }
  return out;
};

/* ================================================================== ROOT */
export default function MRP() {
  const { hasModule } = useSubscription();
  const [tab, setTab] = useState("mrp");

  if (!hasModule("mrp")) {
    return (
      <div className="p-8">
        <div className="card-sharp p-10 text-center">
          <div className="font-heading text-2xl">MRP requires Enterprise Manufacturing</div>
          <a href="/app/subscription" className="inline-block mt-4 px-5 py-2 bg-accent text-white rounded-sm text-sm">Upgrade →</a>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="mrp-page">
      <PageHeader
        eyebrow="Manufacturing"
        title="MRP · Demand & Production Scheduler"
        description="Plan monthly demand, run material requirements planning across horizons, convert shortfalls to PR / PO / WO in one click, and schedule production across machines."
      />
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="rounded-sm">
          <TabsTrigger value="forecast" className="rounded-sm gap-1.5" data-testid="tab-forecast">
            <TrendingUp className="w-3.5 h-3.5" /> Demand Forecast
          </TabsTrigger>
          <TabsTrigger value="mrp" className="rounded-sm gap-1.5" data-testid="tab-mrp">
            <Zap className="w-3.5 h-3.5" /> MRP Run
          </TabsTrigger>
          <TabsTrigger value="scheduler" className="rounded-sm gap-1.5" data-testid="tab-scheduler">
            <Calendar className="w-3.5 h-3.5" /> Production Scheduler
          </TabsTrigger>
        </TabsList>

        <TabsContent value="forecast" className="mt-6"><ForecastTab /></TabsContent>
        <TabsContent value="mrp" className="mt-6"><MrpRunTab /></TabsContent>
        <TabsContent value="scheduler" className="mt-6"><SchedulerTab /></TabsContent>
      </Tabs>
    </div>
  );
}

/* =========================================================== FORECAST TAB */
function ForecastTab() {
  const months = useMemo(() => rollingMonths(6), []);
  const [items, setItems] = useState([]);
  const [grid, setGrid]   = useState({});             // { item_id: { YYYY-MM: qty } }
  const [q, setQ] = useState("");
  const [saving, setSaving] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [notes, setNotes]   = useState({});          // { item_id: string }

  const load = async () => {
    try {
      const [i, f] = await Promise.all([
        api.get("/items"),
        api.get("/mrp/demand-forecast", { params: { from_month: months[0], to_month: months[months.length - 1] } }),
      ]);
      const eligible = (i.data || []).filter(
        (x) => ["finished_good", "wip", "goods"].includes(x.item_type || "goods"),
      );
      setItems(eligible);
      const g = {};
      const n = {};
      (f.data.rows || []).forEach((r) => {
        g[r.item_id] = g[r.item_id] || {};
        g[r.item_id][r.month] = r.qty;
        if (r.notes) n[r.item_id] = r.notes;
      });
      setGrid(g); setNotes(n);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to load"); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const setCell = (item_id, month, val) => {
    setGrid((old) => ({ ...old, [item_id]: { ...(old[item_id] || {}), [month]: val === "" ? "" : Number(val) } }));
  };

  const save = async () => {
    setSaving(true);
    try {
      const rows = [];
      Object.entries(grid).forEach(([item_id, byMonth]) => {
        Object.entries(byMonth || {}).forEach(([month, qty]) => {
          if (qty === "" || qty === null || qty === undefined || Number(qty) === 0) return;
          rows.push({ item_id, month, qty: Number(qty), notes: notes[item_id] || null });
        });
      });
      if (rows.length === 0) { toast.info("Nothing to save."); return; }
      const r = await api.post("/mrp/demand-forecast", { rows });
      toast.success(`${r.data.upserted} forecast row(s) saved`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const aiSuggest = async () => {
    setAiBusy(true);
    try {
      const r = await api.post("/mrp/ai-forecast", null, { params: { months_ahead: 3, look_back_months: 6 } });
      const sugg = r.data.suggestions || [];
      setGrid((old) => {
        const g = { ...old };
        sugg.forEach((s) => {
          g[s.item_id] = { ...(g[s.item_id] || {}) };
          // only fill if cell empty so we don't clobber user input
          if (g[s.item_id][s.month] === undefined || g[s.item_id][s.month] === "" || Number(g[s.item_id][s.month]) === 0) {
            g[s.item_id][s.month] = Number(s.qty);
          }
        });
        return g;
      });
      toast.success(`AI suggested ${sugg.length} row(s) from ${r.data.items_with_history} items with sales history`);
    } catch (e) { toast.error(e?.response?.data?.detail || "AI suggest failed"); }
    finally { setAiBusy(false); }
  };

  const filtered = items.filter((it) => {
    if (!q) return true;
    const s = q.toLowerCase();
    return (it.name || "").toLowerCase().includes(s) || (it.sku || "").toLowerCase().includes(s);
  });

  return (
    <div className="space-y-4">
      <div className="card-sharp p-4 flex flex-wrap items-end gap-3">
        <div className="flex-1 min-w-[220px]">
          <Label>Search FG / WIP item</Label>
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by SKU or name…" className="rounded-sm mt-1" data-testid="forecast-search" />
        </div>
        <Button onClick={aiSuggest} disabled={aiBusy} variant="outline" className="rounded-sm gap-1.5" data-testid="forecast-ai">
          <Sparkles className="w-4 h-4" /> {aiBusy ? "Analysing…" : "AI Suggest (6-mo avg)"}
        </Button>
        <Button onClick={save} disabled={saving} className="rounded-sm bg-accent hover:bg-accent/90 text-white gap-1.5" data-testid="forecast-save">
          <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save Forecast"}
        </Button>
      </div>

      <div className="card-sharp overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full dense text-sm">
            <thead className="bg-muted/30 sticky top-0">
              <tr>
                <th className="text-left px-3 py-2 min-w-[220px]">Item</th>
                {months.map((m) => (
                  <th key={m} className="text-right px-2 py-2 tabular-nums">{m}</th>
                ))}
                <th className="text-left px-3 py-2 min-w-[180px]">Notes</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr><td colSpan={months.length + 2} className="text-center py-10 text-muted-foreground">No matching items.</td></tr>
              )}
              {filtered.map((it) => (
                <tr key={it.id} className="border-b border-border/40" data-testid={`forecast-row-${it.sku}`}>
                  <td className="px-3 py-1.5">
                    <div className="text-xs font-medium">{it.name}</div>
                    <div className="text-[10px] font-mono text-muted-foreground">{it.sku} · {it.item_type || "goods"}</div>
                  </td>
                  {months.map((m) => (
                    <td key={m} className="px-1.5 py-1">
                      <Input
                        type="number"
                        value={grid[it.id]?.[m] ?? ""}
                        onChange={(e) => setCell(it.id, m, e.target.value)}
                        className="rounded-sm text-right tabular-nums h-8 text-xs"
                        data-testid={`forecast-cell-${it.sku}-${m}`}
                      />
                    </td>
                  ))}
                  <td className="px-3 py-1">
                    <Input
                      value={notes[it.id] || ""}
                      onChange={(e) => setNotes((n) => ({ ...n, [it.id]: e.target.value }))}
                      placeholder="Optional…"
                      className="rounded-sm h-8 text-xs"
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <div className="text-xs text-muted-foreground">
        Tip: Save first, then head to <b>MRP Run</b>. The engine will net forecast + open Sales Orders against on-hand, on-order, and planned WOs.
      </div>
    </div>
  );
}

/* =========================================================== MRP RUN TAB */
function MrpRunTab() {
  const [horizon, setHorizon] = useState(3);
  const [includeForecast, setIncludeForecast] = useState(true);
  const [includeSOs, setIncludeSOs] = useState(true);
  const [busy, setBusy] = useState(false);
  const [converting, setConverting] = useState(false);
  const [result, setResult] = useState(null);           // { summary, planned_orders }
  const [selected, setSelected] = useState({});         // { item_id: true }
  const [drawer, setDrawer] = useState(null);
  const [whatIf, setWhatIf] = useState(null);           // { row, month, qty }
  const openDrawer = (r) => {
    // Auto-select the row so drawer convert buttons work in one click
    if (!selected[r.item_id]) setSelected((s) => ({ ...s, [r.item_id]: true }));
    setDrawer(r);
  };

  const runMrp = async () => {
    setBusy(true);
    setSelected({});
    try {
      const r = await api.post("/mrp/run", {
        horizon_months: horizon,
        include_forecast: includeForecast,
        include_open_sos: includeSOs,
      });
      setResult(r.data);
      toast.success(`MRP done — ${r.data.summary.total_planned} planned orders (${r.data.summary.critical_count} critical)`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Run failed"); }
    finally { setBusy(false); }
  };

  const rows = result?.planned_orders || [];
  // Live counts recompute from remaining rows so KPIs never drift after a convert
  const summary = result?.summary || {};
  const liveSummary = useMemo(() => ({
    ...summary,
    total_planned: rows.length,
    buy_count:  rows.filter((r) => r.source === "buy").length,
    make_count: rows.filter((r) => r.source === "make").length,
    long_lead_count: rows.filter((r) => r.is_long_lead).length,
    critical_count:  rows.filter((r) => r.is_critical).length,
    estimated_buy_value: rows.filter((r) => r.source === "buy").reduce((a, r) => a + (r.qty_short * (r.purchase_price || 0)), 0),
  }), [rows, summary]);
  const toggle = (id) => setSelected((s) => ({ ...s, [id]: !s[id] }));
  const toggleAll = (kind) => {
    const target = rows.filter((r) => kind === "all" || r.source === kind);
    const allOn = target.every((r) => selected[r.item_id]);
    setSelected((s) => {
      const n = { ...s };
      target.forEach((r) => { n[r.item_id] = !allOn; });
      return n;
    });
  };
  const selectedRows = rows.filter((r) => selected[r.item_id]);
  const selBuys = selectedRows.filter((r) => r.source === "buy");
  const selMakes = selectedRows.filter((r) => r.source === "make");

  const convert = async (kind) => {
    let target = [];
    let ep = "";
    if (kind === "pr") { target = selBuys; ep = "/mrp/convert-to-pr"; }
    if (kind === "po") { target = selBuys; ep = "/mrp/convert-to-po"; }
    if (kind === "wo") { target = selMakes; ep = "/mrp/convert-to-wo"; }
    if (target.length === 0) { toast.warning("Select at least one eligible item."); return; }
    setConverting(true);
    try {
      const r = await api.post(ep, { planned_items: target, reason: "MRP direct convert" });
      if (kind === "pr") toast.success(`PR ${r.data.pr_number} created · ${r.data.line_count} lines · ${inr(r.data.total_estimated)}`);
      if (kind === "wo") toast.success(`${r.data.count} Work Order(s) created`);
      if (kind === "po") {
        const un = r.data.unassigned_count || 0;
        const created = (r.data.created || []).length;
        toast.success(`${created} PO(s) created` + (un > 0 ? ` · ${un} item(s) skipped (no vendor contract — try Convert-to-PR instead)` : ""));
      }
      // Remove converted rows from result
      setResult((old) => ({
        ...old,
        planned_orders: (old.planned_orders || []).filter((r) => !target.find((t) => t.item_id === r.item_id)),
        summary: { ...old.summary, total_planned: (old.planned_orders || []).length - target.length },
      }));
      setSelected({});
    } catch (e) { toast.error(e?.response?.data?.detail || "Convert failed"); }
    finally { setConverting(false); }
  };

  return (
    <div className="space-y-4">
      {/* run controls */}
      <div className="card-sharp p-4 flex flex-wrap items-end gap-3">
        <div>
          <Label>Horizon (months)</Label>
          <Input type="number" min={1} max={12} value={horizon} onChange={(e) => setHorizon(Number(e.target.value))} className="rounded-sm w-24 mt-1 tabular-nums text-right" data-testid="mrp-horizon" />
        </div>
        <label className="flex items-center gap-2 text-xs mt-6" data-testid="mrp-include-forecast">
          <Checkbox checked={includeForecast} onCheckedChange={setIncludeForecast} /> Include forecast
        </label>
        <label className="flex items-center gap-2 text-xs mt-6" data-testid="mrp-include-sos">
          <Checkbox checked={includeSOs} onCheckedChange={setIncludeSOs} /> Include open Sales Orders
        </label>
        <div className="flex-1" />
        <Button onClick={runMrp} disabled={busy} className="rounded-sm bg-accent hover:bg-accent/90 text-white gap-1.5" data-testid="mrp-run">
          <Play className="w-4 h-4" /> {busy ? "Running…" : "Run MRP"}
        </Button>
      </div>

      {/* summary strip */}
      {result && (
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
          <KPI label="Total planned"  value={liveSummary.total_planned}  />
          <KPI label="To Buy"         value={liveSummary.buy_count}      icon={<ShoppingCart className="w-3.5 h-3.5 text-sky-500" />} />
          <KPI label="To Make"        value={liveSummary.make_count}     icon={<Factory className="w-3.5 h-3.5 text-emerald-500" />} />
          <KPI label="Long-lead"      value={liveSummary.long_lead_count} icon={<Clock className="w-3.5 h-3.5 text-amber-500" />} accent="amber" />
          <KPI label="Critical"       value={liveSummary.critical_count}  icon={<AlertTriangle className="w-3.5 h-3.5 text-rose-500" />} accent="rose" />
          <KPI label="Est. Buy Value" value={inr(liveSummary.estimated_buy_value)} isText />
        </div>
      )}

      {/* bulk action bar */}
      {result && rows.length > 0 && (
        <div className="card-sharp p-3 flex flex-wrap items-center gap-2 sticky top-0 z-10 bg-background">
          <div className="text-xs text-muted-foreground">
            Selected: <b>{selectedRows.length}</b> ({selBuys.length} buy · {selMakes.length} make)
          </div>
          <div className="flex-1" />
          <Button onClick={() => toggleAll("buy")}  variant="ghost" size="sm" className="rounded-sm text-xs" data-testid="sel-all-buy">Select all buy</Button>
          <Button onClick={() => toggleAll("make")} variant="ghost" size="sm" className="rounded-sm text-xs" data-testid="sel-all-make">Select all make</Button>
          <Button onClick={() => convert("pr")} disabled={converting || selBuys.length === 0} size="sm" className="rounded-sm gap-1.5 bg-sky-600 hover:bg-sky-700 text-white" data-testid="convert-pr">
            <FileText className="w-3.5 h-3.5" /> Convert to PR ({selBuys.length})
          </Button>
          <Button onClick={() => convert("po")} disabled={converting || selBuys.length === 0} size="sm" className="rounded-sm gap-1.5 bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="convert-po">
            <ShoppingCart className="w-3.5 h-3.5" /> Convert to PO ({selBuys.length})
          </Button>
          <Button onClick={() => convert("wo")} disabled={converting || selMakes.length === 0} size="sm" className="rounded-sm gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="convert-wo">
            <Factory className="w-3.5 h-3.5" /> Convert to WO ({selMakes.length})
          </Button>
        </div>
      )}

      {/* planned orders table */}
      <div className="card-sharp overflow-hidden">
        {!result && <div className="p-10 text-center text-muted-foreground text-sm">Run MRP to see planned orders.</div>}
        {result && rows.length === 0 && <div className="p-10 text-center text-emerald-600 text-sm">🎉 No shortages — everything is covered by on-hand, POs and planned WOs.</div>}
        {result && rows.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full dense text-sm">
              <thead className="bg-muted/30">
                <tr>
                  <th className="w-8"><Checkbox
                    checked={rows.every((r) => selected[r.item_id])}
                    onCheckedChange={() => toggleAll("all")}
                    aria-label="select all"
                  /></th>
                  <th className="text-left px-3 py-2">Item</th>
                  <th className="text-left px-2 py-2">Source</th>
                  <th className="text-right px-2 py-2">Gross Demand</th>
                  <th className="text-right px-2 py-2">Stock</th>
                  <th className="text-right px-2 py-2">On Order</th>
                  <th className="text-right px-2 py-2">Planned WO</th>
                  <th className="text-right px-2 py-2">Safety</th>
                  <th className="text-right px-2 py-2 font-medium">Net Short</th>
                  <th className="text-left px-2 py-2">Needed by</th>
                  <th className="text-left px-2 py-2">Release by</th>
                  <th className="text-left px-2 py-2">Flags</th>
                  <th className="text-right px-2 py-2">Est. Cost</th>
                  <th className="w-8"></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.item_id} className={`border-b border-border/40 hover:bg-muted/20 ${r.is_critical ? "bg-rose-50/40" : ""}`} data-testid={`plan-row-${r.item_sku || r.item_id}`}>
                    <td className="px-2"><Checkbox checked={!!selected[r.item_id]} onCheckedChange={() => toggle(r.item_id)} /></td>
                    <td className="px-3 py-1.5">
                      <button className="text-left" onClick={() => openDrawer(r)}>
                        <div className="text-xs font-medium hover:underline">{r.item_name}</div>
                        <div className="text-[10px] font-mono text-muted-foreground">{r.item_sku}</div>
                      </button>
                    </td>
                    <td className="px-2 py-1">
                      <span className={`text-[10px] font-mono uppercase px-1.5 py-0.5 rounded-sm ${r.source === "buy" ? clsBadgeBuy : clsBadgeMake}`}>
                        {r.source === "buy" ? "buy" : "make"}
                      </span>
                    </td>
                    <td className="px-2 py-1 text-right tabular-nums text-xs">{fmtNum(r.gross_demand)}</td>
                    <td className="px-2 py-1 text-right tabular-nums text-xs">{fmtNum(r.current_stock)}</td>
                    <td className="px-2 py-1 text-right tabular-nums text-xs">{fmtNum(r.on_order_qty)}</td>
                    <td className="px-2 py-1 text-right tabular-nums text-xs">{fmtNum(r.planned_wo_qty)}</td>
                    <td className="px-2 py-1 text-right tabular-nums text-xs text-muted-foreground">{fmtNum(r.safety_stock)}</td>
                    <td className="px-2 py-1 text-right tabular-nums text-sm font-medium">{fmtNum(r.qty_short)}</td>
                    <td className="px-2 py-1 text-xs">{fmtDate(r.needed_by)}</td>
                    <td className="px-2 py-1 text-xs">{fmtDate(r.release_by)}</td>
                    <td className="px-2 py-1">
                      <div className="flex flex-wrap gap-1">
                        {r.is_critical  && <span className={`text-[10px] px-1.5 py-0.5 rounded-sm ${clsBadgeCrit}`}>critical</span>}
                        {r.is_long_lead && <span className={`text-[10px] px-1.5 py-0.5 rounded-sm ${clsBadgeLong}`}>long-lead {r.lead_time_days}d</span>}
                      </div>
                    </td>
                    <td className="px-2 py-1 text-right text-xs tabular-nums">
                      {r.source === "buy" && r.purchase_price > 0 ? inr(r.qty_short * r.purchase_price) : "—"}
                    </td>
                    <td className="px-2 py-1"><button onClick={() => setDrawer(r)} className="p-1 hover:bg-muted rounded-sm" data-testid={`plan-open-${r.item_sku || r.item_id}`}><ChevronRight className="w-4 h-4" /></button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* row drawer */}
      <Sheet open={!!drawer} onOpenChange={(o) => !o && setDrawer(null)}>
        <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
          {drawer && (
            <>
              <SheetHeader>
                <SheetTitle>{drawer.item_name}</SheetTitle>
                <SheetDescription className="font-mono">{drawer.item_sku}</SheetDescription>
              </SheetHeader>
              <div className="space-y-4 mt-4">
                <div className="flex gap-2">
                  <span className={`text-[10px] font-mono uppercase px-1.5 py-0.5 rounded-sm ${drawer.source === "buy" ? clsBadgeBuy : clsBadgeMake}`}>{drawer.source}</span>
                  {drawer.is_critical  && <span className={`text-[10px] px-1.5 py-0.5 rounded-sm ${clsBadgeCrit}`}>critical</span>}
                  {drawer.is_long_lead && <span className={`text-[10px] px-1.5 py-0.5 rounded-sm ${clsBadgeLong}`}>long-lead {drawer.lead_time_days}d</span>}
                </div>
                {drawer.criticality_reason && (
                  <div className="text-xs text-rose-700 bg-rose-50 border border-rose-200 p-2 rounded-sm">
                    <AlertTriangle className="w-3.5 h-3.5 inline mr-1" /> {drawer.criticality_reason}
                  </div>
                )}
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <Stat label="Gross demand" v={fmtNum(drawer.gross_demand)} />
                  <Stat label="On hand"      v={fmtNum(drawer.current_stock)} />
                  <Stat label="On order (PO)" v={fmtNum(drawer.on_order_qty)} />
                  <Stat label="Planned WO"   v={fmtNum(drawer.planned_wo_qty)} />
                  <Stat label="Safety stock" v={fmtNum(drawer.safety_stock)} />
                  <Stat label="Net short"    v={fmtNum(drawer.qty_short)} highlight />
                  <Stat label="Needed by"    v={fmtDate(drawer.needed_by)} />
                  <Stat label="Release by"   v={fmtDate(drawer.release_by)} />
                  <Stat label="Lead time"    v={`${drawer.lead_time_days || 0} days`} />
                  <Stat label="Purchase price" v={inr(drawer.purchase_price)} />
                </div>
                <div className="pt-2 border-t space-y-2">
                  {drawer.source === "buy" && (
                    <>
                      <Button onClick={() => convert("pr")} className="w-full rounded-sm bg-sky-600 hover:bg-sky-700 text-white justify-start gap-1.5" disabled={!selected[drawer.item_id]}>
                        <FileText className="w-4 h-4" /> {selected[drawer.item_id] ? "Convert selected to PR" : "Tick checkbox to convert"}
                      </Button>
                      <Button onClick={() => convert("po")} className="w-full rounded-sm bg-indigo-600 hover:bg-indigo-700 text-white justify-start gap-1.5" disabled={!selected[drawer.item_id]}>
                        <ShoppingCart className="w-4 h-4" /> {selected[drawer.item_id] ? "Convert selected to PO (skip PR)" : "Tick checkbox to convert"}
                      </Button>
                    </>
                  )}
                  {drawer.source === "make" && (
                    <Button onClick={() => convert("wo")} className="w-full rounded-sm bg-emerald-600 hover:bg-emerald-700 text-white justify-start gap-1.5" disabled={!selected[drawer.item_id]}>
                      <Factory className="w-4 h-4" /> {selected[drawer.item_id] ? "Convert selected to Work Order" : "Tick checkbox to convert"}
                    </Button>
                  )}
                </div>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
      {/* What-If dialog: adjust forecast + re-run MRP without leaving the page */}
      <Dialog open={!!whatIf} onOpenChange={(o) => !o && setWhatIf(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>What-If · Adjust forecast</DialogTitle>
            <DialogDescription>
              Change the demand for {whatIf?.row?.item_name} in the month below, then click <b>Save & Re-run MRP</b>. The MRP engine will re-net available supply vs the new demand and refresh planned orders.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="text-xs text-muted-foreground">
              Current shortfall for this item: <b>{fmtNum(whatIf?.row?.qty_short)}</b> {whatIf?.row?.unit || ""}
              {whatIf?.row?.needed_by && <> · needed by <b>{fmtDate(whatIf.row.needed_by)}</b></>}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Month (YYYY-MM)</Label><Input value={whatIf?.month || ""} onChange={(e) => setWhatIf((w) => ({ ...w, month: e.target.value }))} className="rounded-sm mt-1 tabular-nums" placeholder="2026-09" data-testid="whatif-month" /></div>
              <div><Label>New demand qty</Label><Input type="number" value={whatIf?.qty ?? ""} onChange={(e) => setWhatIf((w) => ({ ...w, qty: e.target.value }))} className="rounded-sm mt-1 tabular-nums text-right" data-testid="whatif-qty" /></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setWhatIf(null)} className="rounded-sm">Cancel</Button>
            <Button
              onClick={async () => {
                if (!whatIf?.month || !/^\d{4}-\d{2}$/.test(whatIf.month)) { toast.error("Month must be YYYY-MM"); return; }
                try {
                  await api.post("/mrp/demand-forecast", { rows: [{
                    item_id: whatIf.row.item_id, month: whatIf.month, qty: Number(whatIf.qty) || 0,
                    notes: "what-if inline adjust",
                  }]});
                  toast.success("Forecast updated · re-running MRP…");
                  setWhatIf(null);
                  await runMrp();
                } catch (e) { toast.error(e?.response?.data?.detail || "Failed to save forecast"); }
              }}
              className="rounded-sm bg-accent hover:bg-accent/90 text-white gap-1.5"
              data-testid="whatif-save">
              <Sparkles className="w-4 h-4" /> Save & Re-run MRP
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* =========================================================== SCHEDULER TAB */
function SchedulerTab() {
  const [data, setData] = useState(null);            // { machines: [...] }
  const [downtime, setDowntime] = useState([]);       // [{machine_id, start_dt, end_dt, type, description, reason_code}]
  const [busy, setBusy] = useState(false);
  const [sub, setSub] = useState("table");

  const load = async () => {
    try {
      const [r, dw] = await Promise.all([
        api.get("/production/schedule"),
        api.get("/maintenance/downtime-windows").catch(() => ({ data: { windows: [] } })),
      ]);
      setData(r.data);
      setDowntime(dw.data.windows || []);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to load"); }
  };
  useEffect(() => { load(); }, []);

  const autoSchedule = async () => {
    setBusy(true);
    try {
      const r = await api.post("/production/auto-schedule", {});
      const pushed = (r.data.skipped_downtime_windows || []).length;
      toast.success(`${r.data.count} Work Order(s) auto-scheduled` + (pushed > 0 ? ` · ${pushed} pushed past maintenance windows` : ""));
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Auto-schedule failed"); }
    finally { setBusy(false); }
  };

  const machines = data?.machines || [];
  // Group downtime windows by machine_id for fast lookup in Gantt
  const downtimeByMachine = useMemo(() => {
    const m = {};
    for (const w of downtime || []) { m[w.machine_id] = m[w.machine_id] || []; m[w.machine_id].push(w); }
    return m;
  }, [downtime]);

  return (
    <div className="space-y-4">
      <div className="card-sharp p-4 flex flex-wrap items-center gap-3">
        <div className="flex-1">
          <div className="font-heading text-sm">Machine schedule · {data?.total_wos || 0} active WOs · {downtime.length} upcoming maintenance windows</div>
          <div className="text-xs text-muted-foreground">Auto-schedule assigns every unassigned WO to the lightest-loaded machine <b>and pushes past any scheduled maintenance window</b>.</div>
        </div>
        <Button onClick={load} variant="outline" size="sm" className="rounded-sm">Refresh</Button>
        <a href="/app/maintenance" className="text-xs text-accent hover:underline">Manage maintenance →</a>
        <Button onClick={autoSchedule} disabled={busy} className="rounded-sm bg-accent hover:bg-accent/90 text-white gap-1.5" data-testid="scheduler-auto">
          <Zap className="w-4 h-4" /> {busy ? "Scheduling…" : "Auto-Schedule"}
        </Button>
      </div>

      <Tabs value={sub} onValueChange={setSub}>
        <TabsList className="rounded-sm">
          <TabsTrigger value="table" className="rounded-sm" data-testid="tab-sched-table">Table view</TabsTrigger>
          <TabsTrigger value="gantt" className="rounded-sm" data-testid="tab-sched-gantt">Gantt view</TabsTrigger>
        </TabsList>

        <TabsContent value="table" className="mt-4">
          <SchedulerTable machines={machines} downtimeByMachine={downtimeByMachine} />
        </TabsContent>
        <TabsContent value="gantt" className="mt-4">
          <SchedulerGantt machines={machines} downtimeByMachine={downtimeByMachine} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function SchedulerTable({ machines, downtimeByMachine = {} }) {
  if (!machines.length) return <div className="card-sharp p-10 text-center text-sm text-muted-foreground">No machines defined. Create machines under Manufacturing → Masters.</div>;
  return (
    <div className="space-y-3">
      {machines.map((m) => {
        const dt = downtimeByMachine[m.id] || [];
        return (
          <div key={m.id} className="card-sharp overflow-hidden" data-testid={`sched-machine-${m.code}`}>
            <div className="px-4 py-3 border-b flex items-center gap-4 bg-muted/20">
              <div className="flex-1">
                <div className="font-heading text-sm">{m.name}</div>
                <div className="text-[10px] font-mono text-muted-foreground">{m.code} · {m.capacity_per_shift || 0}/shift × {m.shifts_per_day || 0} shifts{dt.length > 0 && <> · <span className="text-amber-700">{dt.length} maintenance</span></>}</div>
              </div>
              <div className="text-right">
                <div className={`text-lg font-mono ${m.utilization_pct > 100 ? "text-rose-600" : m.utilization_pct > 80 ? "text-amber-600" : "text-emerald-600"}`}>
                  {m.utilization_pct}%
                </div>
                <div className="text-[10px] font-mono text-muted-foreground">utilization · {m.blocks.length} WOs</div>
              </div>
            </div>
            {dt.length > 0 && (
              <div className="px-4 py-2 border-b bg-amber-50/50 text-[11px]">
                <b className="text-amber-800">Upcoming maintenance:</b>{" "}
                {dt.slice(0, 3).map((w, i) => (
                  <span key={w.id} className="text-amber-700">
                    {i > 0 && " · "}{w.type} ({fmtDate(w.start_dt)} → {fmtDate(w.end_dt)})
                  </span>
                ))}
                {dt.length > 3 && <span className="text-amber-700"> · +{dt.length - 3} more</span>}
              </div>
            )}
            {m.blocks.length === 0 ? (
              <div className="p-6 text-center text-muted-foreground text-xs">No work orders scheduled.</div>
            ) : (
              <table className="w-full dense text-xs">
                <thead className="bg-muted/10">
                  <tr>
                    <th className="text-left px-3 py-1.5">WO#</th>
                    <th className="text-left px-3 py-1.5">Item</th>
                    <th className="text-right px-2 py-1.5">Qty</th>
                    <th className="text-left px-2 py-1.5">Start</th>
                    <th className="text-left px-2 py-1.5">End</th>
                    <th className="text-left px-2 py-1.5">Status</th>
                    <th className="text-left px-2 py-1.5">Priority</th>
                  </tr>
                </thead>
                <tbody>
                  {m.blocks.map((b) => (
                    <tr key={b.wo_id} className="border-t border-border/40 hover:bg-muted/20">
                      <td className="px-3 py-1 font-mono">{b.wo_number}</td>
                      <td className="px-3 py-1">{b.item_name}</td>
                      <td className="px-2 py-1 text-right tabular-nums">{fmtNum(b.qty_planned)}</td>
                      <td className="px-2 py-1">{fmtDate(b.start_dt)}</td>
                      <td className="px-2 py-1">{fmtDate(b.end_dt)}</td>
                      <td className="px-2 py-1"><Badge variant="outline" className="rounded-sm text-[10px]">{b.status}</Badge></td>
                      <td className="px-2 py-1"><Badge variant="outline" className={`rounded-sm text-[10px] ${b.priority === "high" ? "border-rose-300 text-rose-700" : ""}`}>{b.priority}</Badge></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        );
      })}
    </div>
  );
}

function SchedulerGantt({ machines, downtimeByMachine = {} }) {
  if (!machines.length) return <div className="card-sharp p-10 text-center text-sm text-muted-foreground">No machines to visualise.</div>;
  // Timeline domain includes WO blocks AND maintenance windows so nothing is clipped
  const allDates = [
    ...machines.flatMap((m) => m.blocks.flatMap((b) => [b.start_dt, b.end_dt])),
    ...Object.values(downtimeByMachine).flat().flatMap((w) => [w.start_dt, w.end_dt]),
  ].filter(Boolean);
  if (allDates.length === 0) return <div className="card-sharp p-10 text-center text-sm text-muted-foreground">No scheduled work orders to plot.</div>;
  const min = new Date(allDates.reduce((a, b) => (a < b ? a : b)));
  const max = new Date(allDates.reduce((a, b) => (a > b ? a : b)));
  const total = Math.max(1, Math.round((max - min) / 86400000) + 1);
  const dayWidth = 26;
  const width = Math.max(total * dayWidth + 240, 840);

  const LEFT = 240;    // wider left column so utilization % never clips
  const dayHeaders = [];
  for (let i = 0; i < total; i++) {
    const d = new Date(min); d.setDate(d.getDate() + i);
    dayHeaders.push({ d, x: LEFT + i * dayWidth });
  }

  const clr = (b) => {
    if (b.priority === "high") return { fill: "#fee2e2", stroke: "#f43f5e", text: "#9f1239" };
    if (b.status === "completed") return { fill: "#dcfce7", stroke: "#10b981", text: "#065f46" };
    if (b.status === "in_progress") return { fill: "#e0f2fe", stroke: "#0284c7", text: "#075985" };
    return { fill: "#f5f5f4", stroke: "#78716c", text: "#292524" };
  };

  return (
    <div className="card-sharp overflow-auto p-3" data-testid="gantt-container">
      <svg width={width} height={80 + machines.length * 44}>
        <defs>
          {/* Diagonal hatched pattern for maintenance downtime blocks */}
          <pattern id="dt-hatch" width="8" height="8" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
            <rect width="8" height="8" fill="#fef3c7" />
            <line x1="0" y1="0" x2="0" y2="8" stroke="#f59e0b" strokeWidth="3" />
          </pattern>
        </defs>
        {/* header day ticks */}
        <line x1={LEFT} y1={30} x2={width} y2={30} stroke="#e5e5e5" />
        {dayHeaders.map((h, i) => (
          <g key={i}>
            <line x1={h.x} y1={20} x2={h.x} y2={30 + machines.length * 44} stroke={h.d.getDay() === 0 || h.d.getDay() === 6 ? "#f5f5f4" : "#e7e5e4"} />
            <text x={h.x + 3} y={16} className="text-[9px]" fill="#78716c" fontFamily="monospace">{h.d.getDate()}/{h.d.getMonth() + 1}</text>
          </g>
        ))}
        {/* machine rows */}
        {machines.map((m, mi) => {
          const y = 40 + mi * 44;
          const dt = downtimeByMachine[m.id] || [];
          return (
            <g key={m.id}>
              <rect x={0} y={y - 2} width={width} height={40} fill={mi % 2 ? "#fafaf9" : "white"} />
              <text x={8} y={y + 14} className="text-xs" fontWeight="600">{m.code}</text>
              <text x={8} y={y + 28} className="text-[10px]" fill="#78716c">{m.name}</text>
              <text x={LEFT - 10} y={y + 14} textAnchor="end" className="text-[10px]" fill={m.utilization_pct > 100 ? "#e11d48" : "#78716c"}>
                {m.utilization_pct}%
              </text>
              {/* Maintenance downtime rectangles — drawn UNDER the WO blocks */}
              {dt.map((w) => {
                const ws = new Date(w.start_dt), we = new Date(w.end_dt);
                const days = Math.max(0.5, Math.round((we - ws) / 86400000) + 1);
                const startOffset = Math.round((ws - min) / 86400000);
                const x = LEFT + startOffset * dayWidth;
                const rw = Math.max(dayWidth * 0.6, days * dayWidth - 2);
                return (
                  <g key={w.id}>
                    <title>Maintenance · {w.type} · {w.reason_code} · {w.description || ""} · {w.start_dt} → {w.end_dt}</title>
                    <rect x={x} y={y - 2} width={rw} height={40} fill="url(#dt-hatch)" opacity={0.85} />
                    <text x={x + 4} y={y + 12} className="text-[9px]" fill="#78701a" fontWeight="600">MAINT</text>
                  </g>
                );
              })}
              {m.blocks.map((b, bi) => {
                const s = new Date(b.start_dt), e = new Date(b.end_dt);
                const days = Math.max(1, Math.round((e - s) / 86400000) + 1);
                const startOffset = Math.round((s - min) / 86400000);
                const x = LEFT + startOffset * dayWidth;
                const w = Math.max(dayWidth * 0.8, days * dayWidth - 2);
                const c = clr(b);
                return (
                  <g key={b.wo_id}>
                    <title>{b.wo_number} · {b.item_name} · qty {b.qty_planned} · {b.start_dt} → {b.end_dt}</title>
                    <rect x={x} y={y} width={w} height={30} rx={3} fill={c.fill} stroke={c.stroke} strokeWidth="1" />
                    <text x={x + 5} y={y + 12} className="text-[10px]" fill={c.text} fontWeight="600">{b.wo_number}</text>
                    <text x={x + 5} y={y + 24} className="text-[9px]" fill={c.text}>{b.item_name?.slice(0, 20)}</text>
                  </g>
                );
              })}
            </g>
          );
        })}
      </svg>
      <div className="mt-3 flex flex-wrap gap-3 text-[10px]">
        <LegendChip color="#e0f2fe" border="#0284c7" label="In progress" />
        <LegendChip color="#dcfce7" border="#10b981" label="Completed" />
        <LegendChip color="#fee2e2" border="#f43f5e" label="High priority" />
        <LegendChip color="#f5f5f4" border="#78716c" label="Draft / released" />
        <LegendChip color="#fef3c7" border="#f59e0b" label="Maintenance downtime" />
      </div>
    </div>
  );
}

/* ------------------------------------------------------------ tiny helpers */
function KPI({ label, value, icon, accent, isText }) {
  const clsMap = { amber: "text-amber-600", rose: "text-rose-600" };
  return (
    <div className="card-sharp p-3">
      <div className="text-[10px] font-mono uppercase text-muted-foreground flex items-center gap-1">{icon} {label}</div>
      <div className={`text-2xl font-heading mt-1 ${clsMap[accent] || ""}`}>{isText ? value : fmtNum(value || 0)}</div>
    </div>
  );
}
function Stat({ label, v, highlight }) {
  return (
    <div>
      <div className="text-[10px] font-mono uppercase text-muted-foreground">{label}</div>
      <div className={`text-sm tabular-nums ${highlight ? "font-heading text-rose-700" : ""}`}>{v ?? "—"}</div>
    </div>
  );
}
function LegendChip({ color, border, label }) {
  return (
    <div className="flex items-center gap-1.5">
      <div className="w-3 h-3 rounded-sm" style={{ background: color, border: `1px solid ${border}` }} />
      <span className="text-muted-foreground">{label}</span>
    </div>
  );
}
